import { useEffect, useRef, useState } from 'react'
import type { RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { Barcode } from 'shared/components/barcode'
import { Button } from 'shared/components/ui/button'
import { useTranslate } from 'shared/lib/i18n'
import { formatDateTime } from 'shared/lib/utils/date'

/**
 * TezGo's own label for a Parcel (spec §22 Ф6; CONTEXT — Resolving Key, ADR-0002;
 * ticket 48): its `TG…` code large as a barcode and in print, the partner's code
 * `<prefix>:<code>` under it, the partner, the Point (name and ID), the date printed,
 * «TezGo». Every scan field takes the `TG…` code; the partner's code keeps working.
 * Printing writes nothing on the Parcel.
 *
 * Printing is the browser's (`window.print`), like a Bag's label: the page's print
 * style sends only the element marked `data-print-label` to paper. `print` is
 * injected so tests see the call.
 */

export type HubLabelData = RouterOutputs['parcels']['HubLabel']

const printViaBrowser = () => window.print()

/** The label as it goes to paper. Black on white in either theme. */
export function HubLabel({ label }: { label: HubLabelData }) {
	const { t } = useTranslate()
	return (
		<div
			data-print-label
			data-testid='hub-label'
			className='flex w-[360px] flex-col gap-1.5 border-[3px] border-black bg-white p-4 font-mono text-black'
		>
			<Barcode value={label.labelCode} className='h-20 w-full' />
			<div className='break-all text-lg font-extrabold' data-testid='hub-label-code'>
				{label.labelCode}
			</div>
			<div className='text-base font-semibold' data-testid='hub-label-tracking'>
				{label.trackingCode || '—'}
			</div>
			<div data-testid='hub-label-partner'>{t('hubLabel.partner', { partner: label.partnerName || '—' })}</div>
			<div className='text-xl font-semibold' data-testid='hub-label-point'>
				{t('hubLabel.point', { id: label.destinationPoint.id || '—' })}
			</div>
			<div className='flex justify-between'>
				<span data-testid='hub-label-date'>{t('hubLabel.date', { date: formatDateTime(label.printedAt) })}</span>
				<span className='font-extrabold'>TezGo</span>
			</div>
		</div>
	)
}

/** The label on screen with «Печать этикетки»; with `printNow` it goes to the printer once drawn. */
export function HubLabelPrint({
	label,
	printNow = false,
	print = printViaBrowser,
}: {
	label: HubLabelData
	printNow?: boolean
	/** Test seam: the browser's print. */
	print?: () => void
}) {
	const { t } = useTranslate()
	const printed = useRef(false)
	useEffect(() => {
		if (!printNow || printed.current) return
		printed.current = true
		// Let the label paint before the print dialog takes the page.
		const timer = setTimeout(print, 50)
		return () => {
			clearTimeout(timer)
			printed.current = false
		}
	}, [printNow, print])
	return (
		<div className='flex flex-col gap-2'>
			<HubLabel label={label} />
			<div>
				<Button type='button' variant='outline' data-testid='hub-label-print' onClick={print}>
					{t('hubLabel.printAgain')}
				</Button>
			</div>
		</div>
	)
}

async function labelViaTrpc(parcelId: string): Promise<HubLabelData> {
	return trpcClient.parcels.HubLabel.query({ parcelId })
}

/**
 * «Напечатать этикетку TezGo»: asks the server for the label of one Parcel — who may
 * print it is the server's answer — and prints it as soon as it is drawn.
 */
export function PrintHubLabel({
	parcelId,
	loadLabel = labelViaTrpc,
	print,
	buttonLabel,
}: {
	parcelId: string
	/** Test seam: the label's data. Defaults to the real tRPC client. */
	loadLabel?: (parcelId: string) => Promise<HubLabelData>
	print?: () => void
	/** The button's words; «Напечатать этикетку TezGo» by default. */
	buttonLabel?: string
}) {
	const { t } = useTranslate()
	const [label, setLabel] = useState<HubLabelData | null>(null)
	const [state, setState] = useState<'idle' | 'loading' | 'refused'>('idle')
	const ask = async () => {
		setState('loading')
		try {
			setLabel(await loadLabel(parcelId))
			setState('idle')
		} catch {
			setLabel(null)
			setState('refused')
		}
	}
	return (
		<div className='flex flex-col gap-2' data-testid='print-hub-label'>
			{!label && (
				<div>
					<Button type='button' variant='outline' disabled={state === 'loading'} onClick={ask} data-testid='hub-label-ask'>
						{buttonLabel ?? t('hubLabel.print')}
					</Button>
				</div>
			)}
			{state === 'refused' && (
				<p className='text-sm text-destructive' data-testid='hub-label-refused'>
					{t('hubLabel.refused')}
				</p>
			)}
			{label && <HubLabelPrint key={label.labelCode} label={label} printNow print={print} />}
		</div>
	)
}
